import { createFileRoute, Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { BLOG_POSTS } from "@/lib/blog-posts";
import { buildMeta } from "@/lib/seo";
import { Calendar, Clock } from "lucide-react";
import { breadcrumbScript } from "@/lib/breadcrumbs";

export const Route = createFileRoute("/blog/")({
  head: () => {
    const base = buildMeta({
      title: "Blog Tanie Latanie — porady, kierunki i triki na tanie loty",
      description: "Praktyczne poradniki o tanich lotach: kiedy kupować bilety, jak nie przepłacić za bagaż, najlepsze miesiące na popularne kierunki.",
      path: "/blog",
    });
    // Blog + BlogPosting JSON-LD daje listingowi szansę na rich results
    // (Article carousel) i jasno mapuje URL-e wpisów dla crawlerów.
    const blogLd = {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Blog Tanie Latanie",
      url: "https://tanielatanie.pl/blog",
      description:
        "Porady, triki i analizy o tanich lotach z Polski.",
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        description: p.description,
        url: `https://tanielatanie.pl/blog/${p.slug}`,
        datePublished: p.publishedAt,
        articleSection: p.category,
      })),
    };
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(blogLd) },
        breadcrumbScript([
          { name: "Strona główna", path: "/" },
          { name: "Blog", path: "/blog" },
        ]),
      ],
    };
  },
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="min-h-screen">
      <Header />
      <article className="container mx-auto max-w-4xl px-4 py-10">
        <header className="mb-10">
          <h1 className="font-display text-4xl font-bold leading-tight md:text-5xl">
            Blog Tanie Latanie
          </h1>
          <p className="mt-3 text-lg text-muted-foreground">
            Porady, triki i analizy — wszystko, co pomoże Ci wydać mniej na loty.
          </p>
        </header>

        <div className="grid gap-5">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to="/blog/$slug"
              params={{ slug: post.slug }}
              className="group block rounded-xl border border-border/50 bg-card/40 p-5 transition hover:border-primary/40 hover:bg-card/70"
            >
              <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="rounded-full bg-primary/15 px-2.5 py-0.5 font-medium text-primary">
                  {post.category}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(post.publishedAt).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {post.readingTime}
                </span>
              </div>
              <h2 className="font-display text-xl font-bold leading-snug transition-colors group-hover:text-primary md:text-2xl">
                {post.title}
              </h2>
              <p className="mt-2 text-muted-foreground">{post.description}</p>
            </Link>
          ))}
        </div>
      </article>
      <Footer />
    </div>
  );
}
